import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedElement,
  selectSelectedTake3D,
  updateSelectedInteraction,
} from "../../../app/slices/TrainingSlice";
import {
  CreateEmotionBubble,
  IEmotionBubble,
} from "../../../models/interactions/EmotionBubble";
import {
  CreateMessageBubble,
  IMessageBubble,
  defaultMessageBubbleAnimation,
  noMessageBubbleAnimation,
  MessageBubbleAnimation,
} from "../../../models/interactions/MessageBubble";
import { useTrainingLocalization } from "../../trainingLocalization/TrainingLocalizationHooks";
import { IConversation } from "./../../../models/interactions/Conversation";
import ConversationPreviewTextfield from "./ConversationPreviewTextfield";
import { useTranslation } from "react-i18next";
import { PiAnimationButton } from "../animation/PiAnimationPicker";
import PiActorChipSelect from "../components/PiActorChipSelect";
import PiLookModeChipSelect from "../components/PiLookModeChipSelect";
import PiActorPropChipSelect from "../components/PiActorPropChipSelect";
import PiConnectorLabel from "../components/PiConnectorLabel";
import { useConversationCast } from "./ConversationLines.hook";
import { hasLookatTarget } from "../../../models/NoLookat";

export default function ConversationPreviewInput(props: {
  onSubmitClick: () => void;
}) {
  const conversation = useSelector(selectSelectedElement) as IConversation;
  const { getNewKey, changeValue } = useTrainingLocalization();
  const selectedTake = useSelector(selectSelectedTake3D);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { bubbleProps } = useConversationCast(conversation.id);

  const [selectedObjectId, setSelectedObjectId] = useState("player");
  // Start from who the player looks at in the take; "No one" is not carried into new lines.
  const [selectedLookAt, setSelectedLookAt] = useState(() => {
    const playerLookat = selectedTake?.actors.find((actor) => actor.actorId === "player")?.lookat;
    return hasLookatTarget(playerLookat) ? playerLookat! : "";
  });
  const [selectedAnimation, setSelectedAnimation] = useState<MessageBubbleAnimation>(
    { ...defaultMessageBubbleAnimation }
  );
  // Draft look mode for new lines: whole body (false) by default, head only (true).
  const [selectedFinalIK, setSelectedFinalIK] = useState(false);
  const isActorAnchor = selectedTake?.actors.some(
    (actor) => actor.actorId === selectedObjectId
  );
  // Only actors look at things, and never at themselves.
  const effectiveLookAt =
    isActorAnchor && selectedLookAt !== selectedObjectId ? selectedLookAt : "";

  const onSubmit = (text: string) => {
    // request a new key
    var key = getNewKey();

    // fill in the key w/ entered text
    changeValue(key, text);

    const msg: IMessageBubble = CreateMessageBubble([key]);
    msg.npcId = selectedObjectId;
    msg.lookAt = effectiveLookAt;
    msg.finalIK = selectedFinalIK;
    // Props can't play animations.
    msg.animation = isActorAnchor ? selectedAnimation : { ...noMessageBubbleAnimation };

    const cpy = { ...conversation };
    cpy.messages = [...cpy.messages, msg];
    props.onSubmitClick(); // callback to parent

    dispatch(updateSelectedInteraction(cpy));
  };

  const onAnchorChange = (objectId: string) => {
    setSelectedObjectId(objectId);

    const nextAnchorIsActor = selectedTake?.actors.some(
      (actor) => actor.actorId === objectId
    );
    if (!nextAnchorIsActor || objectId === selectedLookAt) setSelectedLookAt("");
  };

  const onEmotionSelect = (emotion: string) => {
    const msg: IEmotionBubble = CreateEmotionBubble();
    msg.npcId = selectedObjectId;
    msg.lookAt = effectiveLookAt;
    msg.finalIK = selectedFinalIK;
    msg.messageBubbleStyle = emotion;
    const cpy = { ...conversation };
    cpy.messages = [...cpy.messages, msg];
    props.onSubmitClick();

    dispatch(updateSelectedInteraction(cpy));
  };

  // Design: Design Studio `screens/lookat-rework/cv2c-chat-closed.html` (composer).
  return (
    <div
      className="flex flex-col gap-2.5 px-4 py-3 rounded-lg border border-solid border-interaction-primary-faded bg-creator-surface"
      data-testid="conversation-composer"
    >
      <ConversationPreviewTextfield onSubmit={onSubmit} onEmotionSelect={onEmotionSelect} />
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
        <div className="flex flex-wrap items-center gap-2.5">
          <PiActorPropChipSelect
            aria-label={String(t("bubble_ui.speaker"))}
            value={selectedObjectId}
            onChange={onAnchorChange}
            availableProps={bubbleProps}
          />
          <PiConnectorLabel text={String(t("conversation_ui.says_to"))} />
          <PiActorChipSelect
            aria-label={String(t("bubble_ui.talks_to"))}
            value={isActorAnchor ? selectedLookAt : ""}
            onChange={setSelectedLookAt}
            excludeActors={[selectedObjectId]}
            allowKeepCurrent
            allowNoActor
            disabled={!isActorAnchor}
          />
          <PiLookModeChipSelect
            value={selectedFinalIK}
            onChange={setSelectedFinalIK}
            disabled={!isActorAnchor || !hasLookatTarget(effectiveLookAt)}
            fit
          />
        </div>
        <span className="w-px h-6 bg-interaction-primary-faded" aria-hidden="true" />
        <PiAnimationButton
          selectedAnimation={isActorAnchor ? selectedAnimation : noMessageBubbleAnimation}
          disabled={!isActorAnchor}
          onChange={(animation) => setSelectedAnimation(animation)}
          fit
        />
      </div>
    </div>
  );
}
